import fetch from 'node-fetch';
import { Role, OptionalRole, Permission } from '../types/role';
import { getAuthToken, getBaseApiUrl } from './helpers';

// Shape of a parsed CSV row sent to the /importroles/validate endpoint.
// Lives in the API client (not the UI parser module) so the API surface is
// self-contained and consumers don't need to know about the UI folder layout.
export interface ImportRoleRecord {
  rowNumber: number;
  sitename: string;
  id: string;
  name: string;
  description: string;
  accountid: string;
  permissionColumns?: Record<string, string>;
}

export interface ImportRolesValidationError {
  rowNumber: number;
  field: string;
  message: string;
}

export interface ImportRolesSkippedRow {
  rowNumber: number;
  accountid: string;
  id: string;
  reason: string;
}

export interface ImportRolesValidationResult {
  totalRows: number;
  validRows: number;
  skippedRows: ImportRolesSkippedRow[];
  validationErrors: ImportRolesValidationError[];
}

export const getRoles = async (accountId: string): Promise<Role[]> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/role`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.json() as Promise<Role[]>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

// url: '/roleTestaccount/role/testRoleId'
export const getRole = async (accountId: string, id: string): Promise<Role> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/role/${id}`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.json() as Promise<Role>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

// url: '/roleTestaccount/role'
export const updateRole = async (newRole: boolean, roleData: OptionalRole, accountId: string): Promise<number> => {
  const method = newRole ? 'PUT' : 'PATCH';
  const requestOptions = {
    method: method,
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify(roleData),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/role`, requestOptions);
  if (response.ok) {
    return response.status;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const getPermissions = async (): Promise<Record<string, Permission>> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/permissions`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.json() as Promise<Record<string, Permission>>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const setUsersForRole = async (accountId: string, roleId: string, userIds: string[]): Promise<number> => {
  const requestOptions = {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify(userIds),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/role/${roleId}/users`, requestOptions);
  if (response.ok) {
    return response.json() as Promise<number>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const removeUserForRole = async (accountId: string, roleId: string, userId: string): Promise<number> => {
  const requestOptions = {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/role/${roleId}/users/${userId}`, requestOptions);
  if (response.ok) {
    return response.json() as Promise<number>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

// Import Roles — the operatorAccountId is the account of the user running the
// import; the import itself is multi-account (target accounts come from each
// CSV row's `accountid`). The path accountId is required by API Gateway routing
// but is not used by the import-roles handler to scope the operation.

export const getImportRolesUploadUrl = async (operatorAccountId: string): Promise<{ uploadUrl: string; fields: Record<string, string>; s3Key: string }> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${operatorAccountId}/importroles/upload`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    return Promise.reject(`Call Failed: ${response.status}`);
  }
  return response.json() as Promise<{ uploadUrl: string; fields: Record<string, string>; s3Key: string }>;
};

export const uploadImportRolesCSV = async (
  file: File,
  uploadUrl: string,
  fields: Record<string, string>,
  onUploadProgress?: (progress: number) => void,
): Promise<void> => {
  const form = new FormData();
  Object.keys(fields).forEach((key) => form.append(key, fields[key]));
  form.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (onUploadProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onUploadProgress((e.loaded / e.total) * 100);
      });
    }
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status === 200 || xhr.status === 204) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.open('POST', uploadUrl);
    xhr.send(form);
  });
};

export const validateImportRoles = async (operatorAccountId: string, headers: string[], records: ImportRoleRecord[]): Promise<ImportRolesValidationResult> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${operatorAccountId}/importroles/validate`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ headers, records }),
  });
  if (!response.ok) {
    return Promise.reject(`Call Failed: ${response.status}`);
  }
  return response.json() as Promise<ImportRolesValidationResult>;
};

export const processImportRoles = async (operatorAccountId: string, s3Key: string): Promise<{ success: boolean; message: string }> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${operatorAccountId}/importroles/process`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ s3Key }),
  });
  if (!response.ok) {
    return Promise.reject(`Call Failed: ${response.status}`);
  }
  return response.json() as Promise<{ success: boolean; message: string }>;
};
